// src/utils/routerHelper.js - 路由导航工具类
import store from '@/store'
import { supportedLanguages, defaultLanguage } from '@/lang/index'
import { stringifyEncryptedQuery } from '@/utils/routeCrypto'

/**
 * 路由导航助手类
 * 提供统一的路由跳转方法，自动处理语言前缀
 */
export class RouterHelper {
  constructor(router) {
    this.router = router
  }

  /**
   * 获取当前语言
   */
  getCurrentLanguage() {
    return store.state.user.lang || defaultLanguage
  }

  /**
   * 构造带语言前缀的路径
   * @param {string} path - 目标路径
   * @param {string} lang - 语言代码（可选）
   */
  buildPath(path, lang = null) {
    const currentLang = lang || this.getCurrentLanguage()
    
    // 如果路径已经包含语言前缀，直接返回
    if (path.match(/^\/[a-z]{2}\//)) {
      return path
    }
    
    // 处理根路径
    if (path === '/') {
      return `/${currentLang}`
    }
    
    // 添加语言前缀
    return `/${currentLang}${path.startsWith('/') ? path : `/${path}`}`
  }

  /**
   * 安全的路由跳转 - push
   * @param {string|object} to - 目标路由
   * @param {object} options - 额外选项
   */
  async push(to, options = {}) {
    try {
      let route
      
      if (typeof to === 'string') {
        route = {
          path: this.buildPath(to),
          ...options
        }
      } else {
        route = {
          ...to,
          path: to.path ? this.buildPath(to.path) : to.path
        }
      }
      
      console.log('RouterHelper push:', route)
      
      await this.router.push(route)
      return { success: true }
    } catch (error) {
      if (error.name === 'NavigationDuplicated') {
        console.log('Navigation duplicated, ignoring:', error.message)
        return { success: true, duplicated: true }
      }
      console.error('Router push failed:', error)
      throw error
    }
  }

  /**
   * 安全的路由跳转 - replace
   * @param {string|object} to - 目标路由
   * @param {object} options - 额外选项
   */
  async replace(to, options = {}) {
    try {
      let route
      
      if (typeof to === 'string') {
        route = {
          path: this.buildPath(to),
          ...options
        }
      } else {
        route = {
          ...to,
          path: to.path ? this.buildPath(to.path) : to.path
        }
      }
      
      console.log('RouterHelper replace:', route)
      
      await this.router.replace(route)
      return { success: true }
    } catch (error) {
      if (error.name === 'NavigationDuplicated') {
        console.log('Navigation duplicated, ignoring:', error.message)
        return { success: true, duplicated: true }
      }
      console.error('Router replace failed:', error)
      throw error
    }
  }

  /**
   * 生成 href 链接
   * @param {string} path - 路径
   * @param {object} query - 查询参数
   */
  buildHref(path, query = {}) {
    const fullPath = this.buildPath(path)
    return fullPath + stringifyEncryptedQuery(query)
  }

  /**
   * 切换语言并保持当前路径
   * @param {string} newLang - 新语言代码
   */
  async switchLanguage(newLang) {
    if (!supportedLanguages.includes(newLang)) {
      throw new Error(`Language ${newLang} is not supported`)
    }

    const currentRoute = this.router.currentRoute
    const currentPath = currentRoute.path
    
    // 替换路径中的语言前缀
    const newPath = currentPath.replace(/^\/[a-z]{2}/, `/${newLang}`)
    
    try {
      await this.router.replace({
        path: newPath,
        query: currentRoute.query,
        hash: currentRoute.hash
      })
      return { success: true, from: currentPath, to: newPath }
    } catch (error) {
      console.error('Language switch navigation failed:', error)
      throw error
    }
  }

  /**
   * 检查是否为当前路由
   * @param {string} path - 要检查的路径
   */
  isCurrentRoute(path) {
    const fullPath = this.buildPath(path)
    return this.router.currentRoute.path === fullPath
  }

  /**
   * 安全的后退
   */
  goBack() {
    if (window.history.length > 1) {
      this.router.go(-1)
    } else {
      this.push('/')
    }
  }

  /**
   * 刷新当前页面
   */
  refresh() {
    this.router.go(0)
  }
}

// 创建全局实例
let routerHelperInstance = null

/**
 * 初始化路由助手
 * @param {VueRouter} router - Vue Router 实例
 */
export function initRouterHelper(router) {
  routerHelperInstance = new RouterHelper(router)
  return routerHelperInstance
}

/**
 * 获取路由助手实例
 */
export function useRouterHelper() {
  if (!routerHelperInstance) {
    throw new Error('RouterHelper not initialized. Call initRouterHelper first.')
  }
  return routerHelperInstance
}

// 便捷导出函数
export const navigateTo = (to, options) => useRouterHelper().push(to, options)
export const redirectTo = (to, options) => useRouterHelper().replace(to, options)
export const buildRoutePath = (path, lang) => useRouterHelper().buildPath(path, lang)
export const buildRouteHref = (path, query) => useRouterHelper().buildHref(path, query)

export default RouterHelper